'use client';
import { signOut } from '@/actions';
import { useTranslation } from '@/app/i18n/client';
import { ChevronDown } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface MemberDropdownProps {
    colorStyle: any;
    attributes: any;
    username: string;
    surveyFormCount?: number;
    surveyEnabled?: boolean;
    subscriptionEnabled?: boolean;
    isGuest?: boolean;
}

const MemberDropdown = ({
    colorStyle,
    attributes,
    username,
    surveyFormCount,
    surveyEnabled = true,
    subscriptionEnabled = true,
    isGuest = false,
}: MemberDropdownProps) => {
    const POPUP_DESIRED_WIDTH = 225;
    const VIEWPORT_MARGIN = 16;

    const { t, lang } = useTranslation();
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);
    const [popupAlign, setPopupAlign] = useState<'left' | 'right'>('left');
    const [popupWidth, setPopupWidth] = useState<number>(POPUP_DESIRED_WIDTH);
    const memberDropdownRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const computePlacement = () => {
        const anchorRect = memberDropdownRef.current?.getBoundingClientRect();
        if (!anchorRect) {
            return { align: 'left' as const, width: POPUP_DESIRED_WIDTH };
        }

        const maxWidth = Math.max(0, window.innerWidth - VIEWPORT_MARGIN * 2);
        let width = Math.min(POPUP_DESIRED_WIDTH, maxWidth);

        const minWidth = Math.min(anchorRect.width, maxWidth);
        width = Math.max(width, minWidth);

        const spaceRight = Math.max(
            0,
            window.innerWidth - VIEWPORT_MARGIN - anchorRect.left,
        );
        const spaceLeft = Math.max(0, anchorRect.right - VIEWPORT_MARGIN);

        if (width <= spaceRight) {
            return { align: 'left' as const, width };
        }
        if (width <= spaceLeft) {
            return { align: 'right' as const, width };
        }

        if (spaceLeft >= spaceRight) {
            return {
                align: 'right' as const,
                width: Math.max(minWidth, spaceLeft),
            };
        }
        return {
            align: 'left' as const,
            width: Math.max(minWidth, spaceRight),
        };
    };

    const toggleDropdown = () => {
        if (isOpen) {
            setIsOpen(false);
            return;
        }

        const placement = computePlacement();
        setPopupAlign(placement.align);
        setPopupWidth(placement.width);
        setIsOpen(true);
    };

    const memberDropdownOption = [
        {
            key: 'my_account',
            label: t('profile.my_account'),
            linkUrl: '/profile',
        },
        {
            key: 'my_surveys',
            label: t('profile.my_surveys'),
            linkUrl: '/profile/surveys',
        },
        {
            key: 'my_order',
            label: t('profile.my_order'),
            linkUrl: '/profile/orders',
        },
        {
            key: 'my_subscriptions',
            label: t('profile.my_subscriptions'),
            linkUrl: '/profile/subscriptions',
        },
        { key: 'sign_out', label: t('profile.sign_out'), linkUrl: '' },
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                memberDropdownRef.current &&
                !memberDropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        let rafId: number | null = null;

        const recalcPlacement = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const placement = computePlacement();
                setPopupAlign(placement.align);
                setPopupWidth(placement.width);
            });
        };

        recalcPlacement();
        window.addEventListener('resize', recalcPlacement);
        window.addEventListener('scroll', recalcPlacement, true);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            window.removeEventListener('resize', recalcPlacement);
            window.removeEventListener('scroll', recalcPlacement, true);
        };
    }, [isOpen]);

    const handleOnValueChanged = (value: string, linkUrl: string) => {
        if (value != 'sign_out') {
            const path = lang ? `/${lang}${linkUrl}` : linkUrl;
            router.push(path);
        }
        setIsOpen(false);
    };

    return (
        <div
            id="header-cp-member-dropdown-container"
            className="relative inline-block w-fit min-w-[100px] border-none"
            ref={memberDropdownRef}
        >
            <button
                id="header-cp-member-dropdown-button"
                onClick={toggleDropdown}
                className="min-w-[100px] max-w-[200px] w-full bg-transparent border-none cursor-pointer focus:outline-none flex flex-row gap-[10px] items-center justify-start"
            >
                <div
                    id="header-cp-member-dropdown-member-name"
                    className="w-fit min-w-[84px] max-w-[184px] font-normal text-[18px] text-ellipsis overflow-hidden text-nowrap text-start border-none"
                    style={{ color: colorStyle?.text || '#3d3d3d' }}
                >
                    {username}
                </div>
                <span
                    id="header-cp-member-dropdown-icon-area"
                    className="w-[16px]"
                >
                    <ChevronDown
                        id="header-cp-member-dropdown-icon"
                        className="h-4 w-4 opacity-90"
                        style={{ color: colorStyle?.text || '#3d3d3d' }}
                    />
                </span>
            </button>
            {isOpen && (
                <div
                    id="header-cp-member-dropdown-popup-options"
                    ref={popupRef}
                    className={`absolute mt-1 ${
                        popupAlign === 'right' ? 'right-0 left-auto' : 'left-0'
                    } bg-white border border-gray-300 rounded shadow-md z-10 p-1`}
                    style={{
                        width: `${popupWidth}px`,
                        maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
                    }}
                >
                    {memberDropdownOption.map((option, index) =>
                        option.key == 'sign_out' ? (
                            // Guest sees Sign In here — Sign Out reads as a real account
                            isGuest ? (
                                <a
                                    id={`header-cp-member-dropdown-popup-options-${index}`}
                                    key={index}
                                    href="/signin"
                                    className="w-full py-2 px-2 flex items-center justify-start hover:bg-gray-200 rounded text-sm"
                                    style={{
                                        color: colorStyle?.text || '#3d3d3d',
                                    }}
                                >
                                    {t('navigation.login')}
                                </a>
                            ) : (
                                <button
                                    id={`header-cp-member-dropdown-popup-options-${index}`}
                                    key={index}
                                    className="w-full py-2 px-2 flex items-center justify-start hover:bg-gray-200 rounded text-sm"
                                    style={{
                                        color: colorStyle?.text || '#3d3d3d',
                                    }}
                                    onClick={async () => {
                                        await signOut();
                                        window.location.href = '/';
                                    }}
                                >
                                    {t('navigation.logout')}
                                </button>
                            )
                        ) : option.key == 'my_surveys' &&
                          (!surveyEnabled ||
                              (surveyFormCount !== undefined &&
                                  surveyFormCount == 0)) ? null : option.key ==
                              'my_subscriptions' &&
                          !subscriptionEnabled ? null : (
                            <div
                                id={`header-cp-member-dropdown-popup-options-${index}`}
                                className="cursor-pointer"
                                key={index}
                            >
                                <div
                                    id={`header-cp-member-dropdown-popup-options-item`}
                                    key={option.key}
                                    onClick={() =>
                                        handleOnValueChanged(
                                            option.key,
                                            option.linkUrl,
                                        )
                                    }
                                    className={`w-full py-2 px-2 flex items-center justify-start hover:bg-gray-200 rounded text-sm`}
                                    style={{
                                        color: colorStyle?.text || '#3d3d3d',
                                    }}
                                >
                                    {option.label}
                                </div>
                            </div>
                        ),
                    )}
                </div>
            )}
        </div>
    );
};

export default MemberDropdown;
